import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BiTargetLock } from "react-icons/bi";
import { AiOutlineClockCircle } from "react-icons/ai";
import { IoNavigate } from "react-icons/io5";

interface InTransitShipment {
  cn_number: string;
  cn_date: string;
  branch: string;
  origin: string;
  destination: string;
  edd: string;
  total_distance: number;
  remaining_distance: number;
  current_address: string;
  last_updated: string;
}

interface DeliveredShipment {
  cn_number: string;
  cn_date: string;
  branch: string;
  origin: string;
  destination: string;
  delivery_date: string;
}

interface LocalShipment {
  cn_number: string;
  cn_date: string;
  branch: string;
  origin: string;
  destination: string;
}

interface TrackingResponse {
  in_transit: InTransitShipment[];
  delivered: DeliveredShipment[];
  local: LocalShipment[];
}

const BRAND_BLUE = '#3B3E81';
const BRAND_RED = '#E23338';

const PublicTrackingPage = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<TrackingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cnNumbers = searchParams.get('cn_numbers');

  useEffect(() => {
    if (cnNumbers) {
      fetchTrackingData(cnNumbers);
    }
  }, [cnNumbers]);

  const fetchTrackingData = async (cnNumbers: string) => {
    setLoading(true);
    setError(null);

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(
        `${apiBaseUrl}/api/v1/public/list?cn_numbers=${cnNumbers}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch tracking data');
      }

      const result: TrackingResponse = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };
  const formatDateTimeHuman = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  

  const titleCase = (str: string): string => {
    if (!str) return str;
    return str.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }


  const hasData = data && (
    data.in_transit.length > 0 ||
    data.delivered.length > 0 ||
    data.local.length > 0
  );

  if (!cnNumbers) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <img src="/logo.png" alt="MRC Logistics" className="h-14 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-slate-900 mb-1">Shipment Tracking</h1>
          <p className="text-sm text-slate-600 mt-4">
            Please visit <a href="https://www.mrcindia.com" className="text-blue-600 text-underline font-bold">www.mrcindia.com</a> and enter your CN numbers to track your shipments.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="MRC Logistics" className="h-10" />
            <div
              className="hidden sm:block h-8 w-px"
              style={{ backgroundColor: 'rgba(148, 163, 184, 0.6)' }}
            />
            <div className="hidden sm:flex flex-col">
              <span className="text-[11px] font-semibold tracking-[0.15em] text-slate-500 uppercase">
              </span>
              <span className="text-xs text-slate-500">
                Tracking
              </span>
            </div>
          </div>
        </div>
        <div
          className="h-0.5 w-full"
          style={{ background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_RED})` }}
        />
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-xl font-semibold text-slate-900">Shipment Tracking</h1>
          </div>

          {data && hasData && (
            <div className="flex flex-wrap gap-3 text-xs">
              <div className="border border-slate-200 bg-white px-3 py-2 rounded-sm flex items-center gap-2">
                <span
                  className="inline-block h-2 w-2 rounded-sm"
                  style={{ backgroundColor: BRAND_BLUE }}
                />
                <span className="font-medium text-slate-700">In Transit</span>
                <span className="text-slate-500">({data.in_transit.length})</span>
              </div>
              <div className="border border-slate-200 bg-white px-3 py-2 rounded-sm flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-sm bg-emerald-500" />
                <span className="font-medium text-slate-700">Delivered</span>
                <span className="text-slate-500">({data.delivered.length})</span>
              </div>
              <div className="border border-slate-200 bg-white px-3 py-2 rounded-sm flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-sm bg-slate-500" />
                <span className="font-medium text-slate-700">Local Trips</span>
                <span className="text-slate-500">({data.local.length})</span>
              </div>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div
              className="animate-spin rounded-full h-7 w-7 border border-slate-300 border-t-transparent"
              style={{ borderTopColor: BRAND_BLUE }}
            />
            <span className="ml-3 text-sm text-slate-600">
              Loading tracking information…
            </span>
          </div>
        )}

        {error && (
          <div
            className="rounded-sm border mb-6 px-4 py-3"
            style={{ borderColor: BRAND_RED, backgroundColor: '#FEF2F2' }}
          >
            <div className="flex">
              <div style={{ color: BRAND_RED }} className="mt-0.5">
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold" style={{ color: BRAND_RED }}>
                  Unable to load data
                </h3>
                <p className="text-sm mt-1 text-red-700">
                  {error}
                </p>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && data && !hasData && (
          <div className="text-center py-16 border border-dashed border-slate-300 bg-white rounded-sm">
            <div className="text-slate-300 mb-4">
                <img src="/images/truck.png" alt="No results" className="h-14 w-18 mx-auto" />
            </div>
            <h3 className="text-base font-medium text-slate-900 mb-1">No results found</h3>
            <p className="text-sm text-slate-600">
              No shipments found for the provided CN numbers.
            </p>
          </div>
        )}

     {/* In Transit Section */}
        {data && data.in_transit.length > 0 && (
        <div className="mb-8">
            <SectionHeader
            label="In Transit"
            count={data.in_transit.length}
            color={BRAND_BLUE}
            />
            <TableWrapper>
            <table className="min-w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
  <tr>
    <Th>CN Number</Th>
    <Th>CN Date</Th>
    <Th>Branch</Th>
    <Th>Route</Th>
    <Th>Latest Location</Th> 
    <Th>Total</Th> 
    <Th>Remaining</Th>
    <Th>EDD</Th>
  </tr>
    </thead>

    <tbody className="bg-white divide-y divide-slate-100">
    {data.in_transit.map((shipment, index) => (
        <tr key={index} className="hover:bg-slate-50">
        <Td className="font-medium text-slate-900">
            {shipment.cn_number}
        </Td>
        <Td>{formatDate(shipment.cn_date)}</Td>
        <Td>{titleCase(shipment.branch)}</Td>

        {/* Route */}
        <Td>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
            {/* Origin */}
            <div className="flex items-center gap-1.5 min-w-0 max-w-[8rem] sm:max-w-[12rem]">
                <BiTargetLock className="h-3.5 w-3.5 flex-shrink-0" style={{ color: BRAND_BLUE }} />
                <span className="truncate">{titleCase(shipment.origin)}</span>
            </div>
            {/* Arrow */}
            <svg className="h-4 w-4 flex-shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
            {/* Destination */}
            <div className="flex items-center gap-1.5 min-w-0 max-w-[8rem] sm:max-w-[12rem]">
                <BiTargetLock className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "#E23338" }} />
                <span className="truncate">{titleCase(shipment.destination)}</span>
            </div>
            </div>
        </Td>

        <Td>
        <div className="flex items-start gap-2">
            <IoNavigate className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0" />
            <div className="min-w-0 max-w-[12rem] sm:max-w-[16rem]">
            <div className="text-slate-800 truncate font-medium">
                {shipment.current_address ? titleCase(shipment.current_address) : '—'}
            </div>
            <div className="text-[11px] leading-4 text-slate-500 truncate">
                {shipment.last_updated
                ? `at ${formatDateTimeHuman(shipment.last_updated)}`
                : 'at —'}
            </div>
            </div>
        </div>
        </Td>
        <Td className="text-right">
            <span
            className="inline-flex items-center px-2 py-0.5 rounded-sm border text-xs font-medium"
            style={{ borderColor: BRAND_BLUE, color: BRAND_BLUE, backgroundColor: "#F3F4FF" }}
            >
            {shipment.total_distance != null ? `${shipment.total_distance} km` : '—'}
            </span>
        </Td>
        <Td className="text-right">
            <span
            className="inline-flex items-center px-2 py-0.5 rounded-sm border text-xs font-medium"
            style={{ borderColor: BRAND_BLUE, color: BRAND_BLUE, backgroundColor: "#F3F4FF" }}
            >
            {shipment.remaining_distance != null ? `${shipment.remaining_distance} km` : '—'}
            </span>
        </Td>
        <Td>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-xs font-medium bg-amber-50 border-amber-200 text-amber-700">
            <AiOutlineClockCircle className="h-3.5 w-3.5" />
            {formatDate(shipment.edd)}
            </span>
        </Td>
            </tr>
    ))}
    </tbody>
 
            </table>
            </TableWrapper>
        </div>
        )}

        {data && data.delivered.length > 0 && (
        <div className="mb-8">
            <SectionHeader
            label="Delivered"
            count={data.delivered.length}
            color="#059669" // emerald-600
            />
            <TableWrapper>
            <table className="min-w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                    <Th>CN Number</Th>
                    <Th>CN Date</Th>
                    <Th>Branch</Th>
                    <Th>Route</Th>
                    <Th>Delivery Date</Th>
                </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                {data.delivered.map((shipment, index) => (
                    <tr key={index} className="hover:bg-slate-50">
                    <Td className="font-medium text-slate-900">
                        {shipment.cn_number}
                    </Td>
                    <Td>{formatDate(shipment.cn_date)}</Td>
                    <Td>{titleCase(shipment.branch)}</Td>
                    <Td>                
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
                        {/* Origin */}
                        <div className="flex items-center gap-1.5 min-w-0 max-w-[8rem] sm:max-w-[12rem]">
                            <BiTargetLock
                            className="h-3.5 w-3.5 flex-shrink-0"
                            style={{ color: BRAND_BLUE }}
                            />
                            <span className="truncate">{titleCase(shipment.origin)}</span>
                        </div>

                        {/* Arrow */}
                        <svg
                            className="h-4 w-4 flex-shrink-0 text-slate-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                            />
                        </svg>

                        {/* Destination */}
                        <div className="flex items-center gap-1.5 min-w-0 max-w-[8rem] sm:max-w-[12rem]">
                            <BiTargetLock
                            className="h-3.5 w-3.5 flex-shrink-0"
                            style={{ color: "#E23338" }}
                            />
                            <span className="truncate">{titleCase(shipment.destination)}</span>
                        </div>
                        </div>
                    </Td>
                    <Td>
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-emerald-50 text-xs font-medium text-emerald-700 border border-emerald-200">
                        <AiOutlineClockCircle className="h-3.5 w-3.5" />
                        {formatDate(shipment.delivery_date)}
                        </span>
                    </Td>
                    </tr>
                ))}
                </tbody>
            </table>
            </TableWrapper>
        </div>
        )}

        {data && data.local.length > 0 && (
        <div className="mb-8">
            <SectionHeader
            label="Local Trips"
            count={data.local.length}
            color="#64748B" // slate-500
            />
            <TableWrapper>
            <table className="min-w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                    <Th>CN Number</Th>
                    <Th>CN Date</Th>
                    <Th>Branch</Th>
                    <Th>Route</Th>
                </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                {data.local.map((shipment, index) => (
                    <tr key={index} className="hover:bg-slate-50">
                    <Td className="font-medium text-slate-900">
                        {shipment.cn_number}
                    </Td>
                    <Td>{formatDate(shipment.cn_date)}</Td>
                    <Td>{titleCase(shipment.branch)}</Td>
                    <Td>
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
                        {/* Origin */}
                        <div className="flex items-center gap-1.5 min-w-0 max-w-[8rem] sm:max-w-[12rem]">
                            <BiTargetLock
                            className="h-3.5 w-3.5 flex-shrink-0"
                            style={{ color: BRAND_BLUE }}
                            />
                            <span className="truncate">{titleCase(shipment.origin)}</span>
                        </div>

                        {/* Arrow */}
                        <svg
                            className="h-4 w-4 flex-shrink-0 text-slate-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                            />
                        </svg>

                        {/* Destination */}
                        <div className="flex items-center gap-1.5 min-w-0 max-w-[8rem] sm:max-w-[12rem]">
                            <BiTargetLock
                            className="h-3.5 w-3.5 flex-shrink-0"
                            style={{ color: "#E23338" }}
                            />
                            <span className="truncate">{titleCase(shipment.destination)}</span>
                        </div>
                        </div>
                    </Td>
                    </tr>
                ))}
                </tbody>
            </table>
            </TableWrapper>
        </div>
        )}
        
      </div>
    </div>
  );
};

interface SectionHeaderProps {
  label: string;
  count: number;
  color: string;
}

const SectionHeader = ({ label, count, color }: SectionHeaderProps) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2">
      <span
        className="inline-block h-4 w-1 rounded-sm"
        style={{ backgroundColor: color }}
      />
      <h2 className="text-xs font-semibold tracking-[0.08em] text-slate-700 uppercase">
        {label}
      </h2>
    </div>
    <span
      className="text-[11px] font-medium px-2 py-0.5 rounded-sm border bg-white"
      style={{ borderColor: 'rgba(148, 163, 184, 0.6)', color: '#475569' }}
    >
      {count} {count === 1 ? 'shipment' : 'shipments'}
    </span>
  </div>
);

const TableWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
    <div className="overflow-x-auto">{children}</div>
  </div>
);

const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <th
    className={`px-5 py-2.5 text-left text-[13px] font-semibold  text-slate-500  ${className}`}
  >
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <td className={`px-5 py-3 whitespace-nowrap text-sm text-slate-600 ${className}`}>
    {children}
  </td>
);

export default PublicTrackingPage;
